import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/* Lets a page (e.g. a Solution or Industry detail page, once it has
 * loaded its own content) tell LISA which specific content_id it's
 * looking at — spec LISA_SPECIFICATION.md §2.2: "LISA's opening line
 * adapts to the page the visitor is on ... implemented here as a
 * page_context field passed with every POST /api/lisa/message." */
interface PageContextValue {
  contentId: string | null;
  entryPoint: string;
}

const PageCtx = createContext<{ value: PageContextValue; setContentId: (id: string | null) => void }>({
  value: { contentId: null, entryPoint: 'homepage' },
  setContentId: () => {},
});

const ENTRY_POINT_BY_PATH: Array<[RegExp, string]> = [
  [/^\/[a-z]{2}\/solutions\/[^/]+$/, 'solution_page'],
  [/^\/[a-z]{2}\/industries\/[^/]+$/, 'industry_page'],
  [/^\/[a-z]{2}\/platform/, 'platform_page'],
  [/^\/[a-z]{2}\/meet-lisa/, 'meet_lisa_page'],
  [/^\/[a-z]{2}\/national-transformation/, 'national_transformation'],
];

export function PageContextProvider({ children }: { children: React.ReactNode }) {
  const [contentId, setContentId] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    // A route change without a matching detail page (e.g. Home) means
    // no page-specific content_id applies until a detail page sets one.
    setContentId(null);
  }, [location.pathname]);

  const entryPoint = ENTRY_POINT_BY_PATH.find(([re]) => re.test(location.pathname))?.[1] ?? 'homepage';

  return <PageCtx.Provider value={{ value: { contentId, entryPoint }, setContentId }}>{children}</PageCtx.Provider>;
}

/** Called by a detail page once its content has loaded, e.g.
 * usePageContentId(`solution:${solution.code}`). */
export function usePageContentId(id: string | null) {
  const { setContentId } = useContext(PageCtx);
  useEffect(() => {
    setContentId(id);
    return () => setContentId(null);
  }, [id, setContentId]);
}

export function useLisaPageContext(): PageContextValue {
  return useContext(PageCtx).value;
}
